import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { useMemo } from "react";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  AdminLinkFacultyPortalUserRequest,
  ResourceV2,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";

type LinkFacultyPortalUserFormValues = {
  firstName?: string;
  lastName?: string;
  email?: string;
};

const { Form } = withTypes<LinkFacultyPortalUserFormValues>();

type LinkFacultyPortalUserDialogProps = {
  resource?: ResourceV2;
  onSuccess: () => void;
  onClose: () => void;
};

export default function LinkFacultyPortalUserDialog({
  resource,
  onSuccess,
  onClose,
}: LinkFacultyPortalUserDialogProps) {
  const { formatMessage: t } = useIntl();
  const { resourceService } = useGlobalContext();

  const init = useMemo<LinkFacultyPortalUserFormValues>(
    () => ({ email: resource?.crmEmail }),
    [resource?.crmEmail]
  );

  const linkFacultyPortalUser = async (
    values: LinkFacultyPortalUserFormValues
  ) => {
    try {
      await resourceService.linkFacultyPortalUser(
        AdminLinkFacultyPortalUserRequest.fromJS({
          ...values,
          resourceId: resource?.id,
          organizationId: resource?.organizationId,
        })
      );

      onSuccess();
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Dialog open={!!resource} onClose={onClose}>
      <Form
        onSubmit={linkFacultyPortalUser}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => {
          return (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Field name="firstName" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="standard"
                          label={t({ id: "firstNameLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="lastName" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="standard"
                          label={t({ id: "lastNameLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="email" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="standard"
                          label={t({ id: "emailLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  {submitError && (
                    <Grid item xs={12}>
                      <Alert severity="error">{submitError}</Alert>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={submitting} onClick={onClose}>
                  {t({ id: "cancelButton" })}
                </Button>

                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  disabled={submitting}
                  startIcon={submitting && <CircularProgress size={16} />}
                >
                  {t({ id: "saveButton" })}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
}
